import { useAppDispatch } from '@/store/hooks';
import { updatePageById } from '@/store/pages/thunks';
import { shallowEqual } from '@/utils/settingsHelpers';
import { useEffect, useRef } from 'react';

export function useSettingsDebounceUpdate<
  V extends object,
  K extends string,
  B extends Record<K, V>,
>({
  value,
  bodyKey,
  currentPageId,
  reduxValue,
}: {
  value: V;
  bodyKey: K;
  currentPageId: string;
  reduxValue?: V | null;
}) {
  const isFirstRender = useRef(true);
  const lastSavedValueRef = useRef<V | null>(null);
  const dispatch = useAppDispatch();

  useEffect(() => {
    isFirstRender.current = true;
    lastSavedValueRef.current = null;
  }, [currentPageId]);

  useEffect(() => {
    if (!currentPageId) return;
    if (!reduxValue) return;

    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (shallowEqual(value, reduxValue)) return;

    const timeout = setTimeout(() => {
      lastSavedValueRef.current = value;
      const body = { [bodyKey]: value } as B;
      dispatch(updatePageById({ pageId: currentPageId!, body }));
    }, 300);

    return () => clearTimeout(timeout);
  }, [value, currentPageId, bodyKey, dispatch]);

  return { lastSavedValueRef };
}
